import { useCallback, useEffect, useRef } from "react";

const DRAG_THRESHOLD = 3;
const SNAP_SETTLE_MS = 360;

function isFinePointerDevice() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

function findClosestChildX(container) {
  const rect = container.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  let closest = null;
  let minDist = Infinity;

  Array.from(container.children).forEach((child) => {
    const childRect = child.getBoundingClientRect();
    const dist = Math.abs(childRect.left + childRect.width / 2 - centerX);
    if (dist < minDist) {
      minDist = dist;
      closest = child;
    }
  });

  return closest;
}

function findClosestChildY(container) {
  const topY = container.getBoundingClientRect().top;
  let closest = null;
  let minDist = Infinity;

  Array.from(container.children).forEach((child) => {
    const dist = Math.abs(child.getBoundingClientRect().top - topY);
    if (dist < minDist) {
      minDist = dist;
      closest = child;
    }
  });

  return closest;
}

/**
 * Mouse drag-to-scroll for desktop web only. Touch / phone scrolling is unchanged.
 */
export function useDesktopDragScroll({
  ref: targetRef,
  axis = "x",
  enabled = true,
  onDragStart,
  onDragMove,
  onDragEnd,
  onWheelScroll,
} = {}) {
  const internalRef = useRef(null);
  const ref = targetRef ?? internalRef;
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const finePointerRef = useRef(isFinePointerDevice());
  const settleTimerRef = useRef(null);

  useEffect(() => {
    finePointerRef.current = isFinePointerDevice();
    const media = window.matchMedia("(hover: hover) and (pointer: fine)");
    const onChange = (event) => {
      finePointerRef.current = event.matches;
    };

    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const restoreSnap = useCallback((el) => {
    if (!el) return;
    el.classList.remove("is-desktop-dragging");
    el.style.scrollSnapType = "";
    el.style.scrollBehavior = "";
  }, []);

  const snapToNearest = useCallback((el) => {
    if (!el) return;

    const closest = axis === "x" ? findClosestChildX(el) : findClosestChildY(el);
    if (!closest) {
      restoreSnap(el);
      return;
    }

    el.style.scrollBehavior = "smooth";
    closest.scrollIntoView({
      behavior: "smooth",
      inline: axis === "x" ? "center" : "nearest",
      block: axis === "y" ? "start" : "nearest",
    });

    if (settleTimerRef.current) {
      window.clearTimeout(settleTimerRef.current);
    }

    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null;
      restoreSnap(el);
      onDragEnd?.();
    }, SNAP_SETTLE_MS);
  }, [axis, onDragEnd, restoreSnap]);

  const finishDrag = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || (event && drag.pointerId !== event.pointerId)) return;

    const el = ref.current;
    const moved = drag.moved;
    dragRef.current = null;

    if (el) {
      try {
        el.releasePointerCapture(drag.pointerId);
      } catch {
        // Pointer may already be released.
      }
    }

    if (!moved) {
      restoreSnap(el);
      return;
    }

    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);

    snapToNearest(el);
  }, [restoreSnap, snapToNearest]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;

    const onPointerDown = (event) => {
      if (!finePointerRef.current || event.pointerType !== "mouse" || event.button !== 0) {
        return;
      }

      if (event.target.closest(".profile-map-card, button, a, input, textarea, select, label")) {
        return;
      }

      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: el.scrollLeft,
        startScrollTop: el.scrollTop,
        moved: false,
      };

      el.setPointerCapture(event.pointerId);
      onDragStart?.();
    };

    const onPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      const delta = axis === "x" ? deltaX : deltaY;

      if (!drag.moved && Math.abs(delta) < DRAG_THRESHOLD) return;

      if (!drag.moved) {
        drag.moved = true;
        el.classList.add("is-desktop-dragging");
        el.style.scrollSnapType = "none";
        el.style.scrollBehavior = "auto";
      }

      event.preventDefault();

      if (axis === "x") {
        el.scrollLeft = drag.startScrollLeft - deltaX;
      } else {
        el.scrollTop = drag.startScrollTop - deltaY;
      }

      onDragMove?.();
    };

    const onPointerUp = (event) => finishDrag(event);

    const onWheel = (event) => {
      if (!finePointerRef.current || !onWheelScroll) return;
      onWheelScroll(event, el);
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);

    if (onWheelScroll) {
      el.addEventListener("wheel", onWheel, { passive: false });
    }

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);

      if (onWheelScroll) {
        el.removeEventListener("wheel", onWheel);
      }

      if (settleTimerRef.current) {
        window.clearTimeout(settleTimerRef.current);
      }
    };
  }, [axis, enabled, finishDrag, onDragMove, onDragStart, onWheelScroll]);

  return { ref, suppressClickRef };
}
